import { CommonModule } from "@angular/common";
import { HttpClientModule } from "@angular/common/http";
import { InjectionToken, ModuleWithProviders, NgModule } from "@angular/core";
import { NgxsModule } from "@ngxs/store";
import { RxStomp, RxStompConfig } from "@stomp/rx-stomp";
import { MultiWebsocketService } from "./services/multi-websocket.service";
import { RxStompService } from "./services/websocket-connection-status.service";
import { WebsocketService } from "./services/websocket.service";

export const WEBSOCKET_CONFIGURATION = new InjectionToken<SharedWebsocketModuleConfig>("Websocket configuration");

export const RX_STOMP_CONNECTIONS = new InjectionToken<RxStompConnection[]>("RxStomp connections");

export interface WebsocketEndpointConfig {
    url: string;
    lazyConnect?: boolean;
}

export interface SharedWebsocketModuleConfig {
    endpoint: string | WebsocketEndpointConfig | Array<string | WebsocketEndpointConfig>;
    authorizationTokenProvider?: () => Promise<string>;
}

export interface RxStompConnection {
    endpoint: string;
    service: RxStompService;
    lazyConnect: boolean;
}

export function rxStompServiceFactory(config: RxStompConfig, lazyConnect = false) {
    const rxStomp = new RxStompService();

    rxStomp.configure(config);
    if (!lazyConnect) {
        rxStomp.activate();
    }

    return rxStomp;
}

@NgModule({
    imports: [CommonModule, NgxsModule, HttpClientModule],
})
export class SharedWebsocketModule {
    private static rxStompConfigFactory(endpoint: string, config: SharedWebsocketModuleConfig): RxStompConfig {
        return {
            brokerURL: endpoint,
            beforeConnect: async () => {
                if (!config.authorizationTokenProvider) {
                    return;
                }
                // TODO: Create true authorization DTM-954
                // client.stompClient.brokerURL += `?jwt=${await config.authorizationTokenProvider()}`;
            },
        };
    }

    private static rxStompConnectionsFactory(config: SharedWebsocketModuleConfig): RxStompConnection[] {
        return SharedWebsocketModule.normalizeEndpoints(config.endpoint).map((endpointConfig) => ({
            endpoint: endpointConfig.url,
            lazyConnect: endpointConfig.lazyConnect ?? false,
            service: rxStompServiceFactory(
                SharedWebsocketModule.rxStompConfigFactory(endpointConfig.url, config),
                endpointConfig.lazyConnect ?? false
            ),
        }));
    }

    private static normalizeEndpoints(
        endpoint: string | WebsocketEndpointConfig | Array<string | WebsocketEndpointConfig>
    ): WebsocketEndpointConfig[] {
        const endpoints = Array.isArray(endpoint) ? endpoint : [endpoint];

        return endpoints.map((item) => SharedWebsocketModule.toEndpointConfig(item));
    }

    private static toEndpointConfig(endpoint: string | WebsocketEndpointConfig): WebsocketEndpointConfig {
        if ((endpoint as WebsocketEndpointConfig).url !== undefined) {
            return endpoint as WebsocketEndpointConfig;
        }

        return { url: endpoint as string, lazyConnect: false };
    }

    private static firstRxStompServiceFactory(connections: RxStompConnection[]): RxStompService {
        return connections[0].service;
    }

    public static forRoot(): ModuleWithProviders<SharedWebsocketModule> {
        return {
            ngModule: SharedWebsocketModule,
            providers: [
                {
                    provide: RX_STOMP_CONNECTIONS,
                    useFactory: SharedWebsocketModule.rxStompConnectionsFactory,
                    deps: [WEBSOCKET_CONFIGURATION],
                },
                {
                    provide: RxStompService,
                    useFactory: SharedWebsocketModule.firstRxStompServiceFactory,
                    deps: [RX_STOMP_CONNECTIONS],
                },
                WebsocketService,
                MultiWebsocketService,
            ],
        };
    }

    public static forTest(): ModuleWithProviders<SharedWebsocketModule> {
        const rxStompService = new RxStompService();

        return {
            ngModule: SharedWebsocketModule,
            providers: [
                {
                    provide: RX_STOMP_CONNECTIONS,
                    useValue: [
                        {
                            endpoint: "ws://localhost:15674/ws",
                            service: rxStompService,
                            lazyConnect: false,
                        },
                    ],
                },
                {
                    provide: RxStompService,
                    useValue: new RxStomp(),
                },
                WebsocketService,
                MultiWebsocketService,
            ],
        };
    }
}
